/* [PATCH prodaja-pregled-fe v1] Pregled v aplikaciji Prodaja (NACRT_prodaja_pregled_v1_20260928.md, D1–D5).
   A prodaja (izdani v documents) · B prodajni lijak in C artikli samo, ko podjetje uporablja Izdajo · D terjatve z Banko.
   Gradniki in pomožne funkcije iz PregledPage (skupni videz). Vse številke iz GET /prodaja-pregled/{cid}/* (samo branje). */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend, LineChart, Line, ComposedChart, Bar } from 'recharts';
import { X, Loader, Info, ExternalLink } from 'lucide-react';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { fmt } from '../lib/izdajaZneski';
import {
  Preset, Primerjava, BARVA, PRESETI, iso, fmtDatum, mesecOznaka, eur, kratko, obdobjeZa, minusLeto,
  Delta, Kartica, Okvir, Prazno, Lestvica, koncentracija, tooltipStil,
} from './PregledPage';

type RazclP = 'konto' | 'sm' | 'projekt';
const RAZCL_P: { id: RazclP; naziv: string }[] = [
  { id: 'konto', naziv: 'Konto' }, { id: 'sm', naziv: 'Stroškovno mesto' }, { id: 'projekt', naziv: 'Projekt' },
];
const TIP_IME: Record<string, string> = { ponudba: 'Ponudba', predracun: 'Predračun', narocilo: 'Naročilo', avans: 'Avansni račun', racun: 'Račun' };
const LIJAK: { k: string; naziv: string; opis: string }[] = [
  { k: 'odprte_ponudbe', naziv: 'Odprte ponudbe', opis: 'izdane ali sprejete, še brez računa, veljavne' },
  { k: 'potecejo_7', naziv: 'Ponudbe, ki potečejo v 7 dneh', opis: 'del odprtih ponudb' },
  { k: 'potekle_ponudbe', naziv: 'Potekle ponudbe', opis: 'veljavnost je minila, računa ni' },
  { k: 'narocila', naziv: 'Naročila v izvedbi', opis: 'odprta, ne v celoti realizirana' },
  { k: 'predracuni', naziv: 'Predračuni, ki čakajo plačilo', opis: 'neplačani ali delno plačani, veljavni' },
  { k: 'avansi', naziv: 'Neporačunani avansi', opis: 'izdani ali poslani, še ne poračunani' },
];

export function ProdajaPregledPage() {
  const { selectedCompany } = useAuthStore();
  const cid = selectedCompany?.id;
  const navigate = useNavigate();

  const [preset, setPreset] = useState<Preset>('letos');
  const [odMeri, setOdMeri] = useState('');
  const [doMeri, setDoMeri] = useState('');
  const [primerjava, setPrimerjava] = useState<Primerjava>('lani');
  const [bruto, setBruto] = useState(false);
  const [samoPotrjeni, setSamoPotrjeni] = useState(false);
  const [razcl, setRazcl] = useState<RazclP>('konto');
  const [podatki, setPodatki] = useState<any>(null);
  const [nalaganje, setNalaganje] = useState(false);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [drill, setDrill] = useState<any>(null);

  const [od, doDat] = useMemo<[string, string]>(
    () => (preset === 'po_meri' && odMeri && doMeri ? [odMeri, doMeri] : obdobjeZa(preset, new Date())),
    [preset, odMeri, doMeri]);

  const nalozi = useCallback(async () => {
    if (!cid) return;
    setNalaganje(true); setNapaka(null);
    const pot = `/prodaja-pregled/${cid}`;
    const osn: any = { od, 'do': doDat, samo_potrjeni: samoPotrjeni };
    const b: any = { ...osn, bruto };
    const lani: any = { ...b, od: minusLeto(od), 'do': minusLeto(doDat) };
    try {
      const r = await Promise.all([
        api.get(`${pot}/stanje`),
        api.get(`${pot}/povzetek`, { params: b }),
        api.get(`${pot}/serija`, { params: { ...b, razclenitev: 'mesec' } }),
        api.get(`${pot}/serija`, { params: { ...lani, razclenitev: 'mesec' } }),
        api.get(`${pot}/serija`, { params: { ...b, razclenitev: 'partner', limit: 10 } }),
        api.get(`${pot}/serija`, { params: { ...osn, razclenitev: razcl, limit: 12 } }),
        api.get(`${pot}/kupci-gibanje`, { params: b }),
        api.get(`${pot}/lijak`, { params: { od, 'do': doDat } }),
        api.get(`${pot}/artikli`, { params: { od, 'do': doDat, limit: 10 } }),
      ]);
      setPodatki({ stanje: r[0].data, povzetek: r[1].data, mes: r[2].data, mesL: r[3].data, kupci: r[4].data,
                   razcl: r[5].data, gibanje: r[6].data, lijak: r[7].data, artikli: r[8].data });
    } catch (e: any) {
      setNapaka(e?.response?.data?.detail || 'Podatkov ni bilo mogoče naložiti.');
    } finally {
      setNalaganje(false);
    }
  }, [cid, od, doDat, bruto, samoPotrjeni, razcl]);

  useEffect(() => { nalozi(); }, [nalozi]);

  useEffect(() => {
    if (!drill || !drill.nalaganje || !cid) return;
    const pot = `/prodaja-pregled/${cid}`;
    (drill.lijak
      ? api.get(`${pot}/lijak-dokumenti`, { params: { skupina: drill.lijak, od, 'do': doDat } })
      : api.get(`${pot}/racuni`, { params: { od, 'do': doDat, samo_potrjeni: samoPotrjeni, ...drill.params } }))
      .then((r: any) => setDrill((d: any) => d && { ...d, podatki: r.data, nalaganje: false }))
      .catch((e: any) => setDrill((d: any) => d && { ...d, napaka: e?.response?.data?.detail || 'Seznama ni bilo mogoče naložiti.', nalaganje: false }));
  }, [drill, cid, od, doDat, samoPotrjeni]);
  const odpri = (naslov: string, params: any) => setDrill({ naslov, params, podatki: null, nalaganje: true });
  const odpriLijak = (naslov: string, skupina: string) => setDrill({ naslov, lijak: skupina, podatki: null, nalaganje: true });

  const mesecni = useMemo(() => {
    if (!podatki) return [];
    const p = podatki.mes.tocke || []; const pl = podatki.mesL?.tocke || [];
    let k = 0; let kl = 0;
    return p.map((t: any, i: number) => {
      const lv = pl[i]?.vrednost || 0; k += t.vrednost; kl += lv;
      return { kljuc: t.kljuc, oznaka: mesecOznaka(t.kljuc), prihodki: t.vrednost, prihodkiL: lv, kum: k, kumL: kl };
    });
  }, [podatki]);

  if (!cid) return <div className="p-6 text-sm text-gray-500">Najprej izberite podjetje v zgornjem izbirniku.</div>;

  const pv = podatki?.povzetek;
  const ref = pv ? (primerjava === 'lani' ? pv.lani : primerjava === 'prejsnje' ? pv.prejsnje : null) : null;
  const pokaziLani = primerjava === 'lani' && !!pv && pv.lani.st_racunov + pv.lani.st_dobropisov > 0;
  const st = podatki?.stanje;
  const gib = podatki?.gibanje;
  const lij = podatki?.lijak;
  const art = podatki?.artikli;

  return (
    <div className="p-4 lg:p-6 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Pregled prodaje</h1>
          <div className="text-xs text-gray-500">{(selectedCompany as any)?.name} · {fmtDatum(od)} – {fmtDatum(doDat)}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <div className="flex flex-wrap rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            {PRESETI.map((p) => (
              <button key={p.id} type="button" onClick={() => setPreset(p.id)}
                className={'px-2.5 py-1.5 ' + (preset === p.id ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800')}>
                {p.naziv}
              </button>
            ))}
          </div>
          {preset === 'po_meri' && (
            <span className="flex items-center gap-1">
              <input type="date" value={odMeri} onChange={(e) => setOdMeri(e.target.value)} className="px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900" />
              –
              <input type="date" value={doMeri} onChange={(e) => setDoMeri(e.target.value)} className="px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900" />
            </span>
          )}
          <select value={primerjava} onChange={(e) => setPrimerjava(e.target.value as Primerjava)}
            className="px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <option value="lani">Primerjava: isto obdobje lani</option>
            <option value="prejsnje">Primerjava: prejšnje obdobje</option>
            <option value="brez">Brez primerjave</option>
          </select>
          <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={bruto} onChange={(e) => setBruto(e.target.checked)} /> z DDV</label>
          <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={samoPotrjeni} onChange={(e) => setSamoPotrjeni(e.target.checked)} /> samo potrjeni</label>
          {nalaganje && <Loader className="w-4 h-4 animate-spin text-blue-500" />}
        </div>
      </div>

      {napaka && <div className="rounded-lg bg-rose-50 dark:bg-rose-950 text-rose-700 dark:text-rose-300 text-sm p-3">{napaka}</div>}

      {st && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500 bg-gray-100 dark:bg-gray-800/60 rounded-lg px-3 py-2">
          <Info className="w-3.5 h-3.5" />
          <span>Zadnji vnos izdanih: {fmtDatum(st.zadnji_vnos_izdani)}</span>
          <span>Vir: vsi izdani računi (uvoz, nalaganje in Izdaja)</span>
          {!st.ima_izdajo && <span>Prodajni lijak in artikli se prikažejo, ko podjetje začne izdajati dokumente v Izdaji.</span>}
        </div>
      )}

      {pv && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            <Kartica naslov={'Prihodki' + (bruto ? ' (z DDV)' : '')} vrednost={eur(pv.obdobje.prihodki)}
              opomba={`${pv.obdobje.st_racunov} računov · ${pv.obdobje.st_dobropisov} dobropisov`}
              onClick={() => odpri('Izdani računi', {})}>
              {ref && <Delta zdaj={pv.obdobje.prihodki} prej={ref.prihodki} st={ref.st_racunov + ref.st_dobropisov} />}
            </Kartica>
            <Kartica naslov="Število računov" vrednost={String(pv.obdobje.st_racunov)}>
              {ref && <Delta zdaj={pv.obdobje.st_racunov} prej={ref.st_racunov} st={ref.st_racunov} />}
            </Kartica>
            <Kartica naslov={'Povprečni račun' + (bruto ? ' (z DDV)' : '')} vrednost={eur(pv.obdobje.povprecni)} opomba="prihodki / število računov">
              {ref && <Delta zdaj={pv.obdobje.povprecni} prej={ref.povprecni} st={ref.st_racunov} />}
            </Kartica>
            <Kartica naslov="Kupcev z računom" vrednost={String(pv.obdobje.kupcev)}>
              {ref && <Delta zdaj={pv.obdobje.kupcev} prej={ref.kupcev} st={ref.st_racunov} />}
            </Kartica>
            <Kartica naslov="Izstopni DDV" vrednost={pv.obdobje.ddv_na_voljo ? eur(pv.obdobje.izstopni_ddv) : '—'}
              opomba={pv.obdobje.ddv_na_voljo ? ('os: datum DDV' + (pv.obdobje.oss ? ` · OSS ločeno ${eur(pv.obdobje.oss)}` : '')) : 'podjetje ni zavezanec za DDV'} />
            <Kartica naslov="Terjatve" vrednost="—" opomba="na voljo z aplikacijo Banka" />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <Okvir naslov="Prihodki po mesecih" opomba={(bruto ? 'z DDV' : 'brez DDV') + ' · os: datum storitve · klik na stolpec odpre račune' + (pokaziLani ? ' · črtkano: lani' : '')}>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={mesecni}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="oznaka" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={kratko} tick={{ fontSize: 11 }} width={48} />
                    <Tooltip formatter={(v: any) => eur(v)} contentStyle={tooltipStil} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="prihodki" name="Prihodki" fill={BARVA.prihodki} cursor="pointer"
                      onClick={(d: any) => { const e = d?.payload || d; odpri('Izdani računi — ' + e.oznaka, { mesec: e.kljuc }); }} />
                    {pokaziLani && <Line dataKey="prihodkiL" name="Prihodki lani" stroke={BARVA.prihodki} strokeDasharray="5 4" dot={false} />}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </Okvir>
            <Okvir naslov="Kumulativa v obdobju" opomba={pokaziLani ? 'polno: letos · črtkano: lani' : 'seštevek po mesecih'}>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={mesecni}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="oznaka" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={kratko} tick={{ fontSize: 11 }} width={48} />
                    <Tooltip formatter={(v: any) => eur(v)} contentStyle={tooltipStil} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line dataKey="kum" name="Prihodki" stroke={BARVA.prihodki} strokeWidth={2} dot={false} />
                    {pokaziLani && <Line dataKey="kumL" name="Prihodki lani" stroke={BARVA.prihodki} strokeDasharray="5 4" dot={false} />}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Okvir>
          </div>

          {/* [PATCH prodaja-pregled-fe v2] lijak in artikli pod grafi, nad kupci */}
          {lij && lij.na_voljo && (
            <Okvir naslov="Prodajni lijak" opomba="dokumenti iz Izdaje · stanje danes; pretvorba za ponudbe, izdane v obdobju · klik odpre dokumente">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                <button type="button" onClick={() => odpriLijak('Ponudbe, izdane v obdobju', 'ponudbe_obdobje')}
                  className="text-left rounded-lg border border-gray-200 dark:border-gray-800 p-3 hover:border-blue-400">
                  <div className="text-xs text-gray-500">Pretvorba ponudb v račun</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">
                    {lij.pretvorba.delez == null ? '—' : Math.round(lij.pretvorba.delez * 100) + ' %'}
                  </div>
                  <div className="text-[11px] text-gray-400">
                    {lij.pretvorba.pretvorjenih} od {lij.pretvorba.ponudb} ponudb
                    {lij.pretvorba.mediana_dni != null ? ` · mediana ${lij.pretvorba.mediana_dni} dni` : ''}
                  </div>
                </button>
                {LIJAK.map((s) => (
                  <button key={s.k} type="button" onClick={() => odpriLijak(s.naziv, s.k)}
                    className="text-left rounded-lg border border-gray-200 dark:border-gray-800 p-3 hover:border-blue-400">
                    <div className="text-xs text-gray-500">{s.naziv}</div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">{lij[s.k].st} · {eur(lij[s.k].osnova)}</div>
                    <div className="text-[11px] text-gray-400">{s.opis} · brez DDV</div>
                  </button>
                ))}
              </div>
            </Okvir>
          )}

          {art && art.na_voljo && (
            <Okvir naslov="Najbolje prodajani artikli" opomba="računi in dobropisi iz Izdaje · os: datum dokumenta · brez DDV">
              <Lestvica tocke={art.tocke} ostalo={art.ostalo} barva={BARVA.prihodki}
                oznaka={(t: any) => `${t.sifra ? t.sifra + ' · ' : ''}${t.naziv}${t.kolicina != null ? ` · ${fmt(t.kolicina, 2)} ${t.enota || ''}` : ''}`} />
            </Okvir>
          )}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <Okvir naslov="Največji kupci" opomba={koncentracija(podatki.kupci) || 'prihodki po kupcu'}>
              <Lestvica tocke={podatki.kupci.tocke} ostalo={podatki.kupci.ostalo} barva={BARVA.prihodki}
                onKlik={(t: any) => odpri('Izdani računi — ' + t.naziv, { partner: t.kljuc })} />
            </Okvir>
            <Okvir naslov="Novi in izgubljeni kupci"
              opomba={`novi: brez računa v 12 mesecih pred obdobjem · izgubljeni: kupovali ${fmtDatum(gib?.prejsnje_od)} – ${fmtDatum(gib?.prejsnje_do)}, v obdobju ne`}>
              {gib && (!gib.zanesljivo_novi || !gib.zanesljivo_izgubljeni) && (
                <div className="mb-3 text-[11px] rounded bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 px-2 py-1">
                  Izdani računi segajo šele od {fmtDatum(gib.najstarejsi_racun)} — {!gib.zanesljivo_novi ? 'novih kupcev ni mogoče zanesljivo oceniti' : ''}{!gib.zanesljivo_novi && !gib.zanesljivo_izgubljeni ? '; ' : ''}{!gib.zanesljivo_izgubljeni ? 'izgubljenih ni mogoče zanesljivo oceniti' : ''}.
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">Novi: {gib?.novi.st || 0} · {eur(gib?.novi.vrednost)}</div>
                  {gib?.zanesljivo_novi ? /* [PATCH pregled-lestvica v1] */ <Lestvica tocke={gib?.novi.tocke} ostalo={gib?.novi.ostalo} barva={BARVA.prihodki}
                    onKlik={(t: any) => odpri('Izdani računi — ' + t.naziv, { partner: t.kljuc })} /> : <Prazno besedilo="Seznam ni prikazan — podatki ne segajo 12 mesecev pred obdobjem." />}
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">Izgubljeni: {gib?.izgubljeni.st || 0} · {eur(gib?.izgubljeni.vrednost)} (prej)</div>
                  {gib?.zanesljivo_izgubljeni ? <Lestvica tocke={gib?.izgubljeni.tocke} ostalo={gib?.izgubljeni.ostalo} barva={BARVA.rok}
                    onKlik={(t: any) => odpri('Izdani računi — ' + t.naziv + ' (prejšnje obdobje)', { partner: t.kljuc, od: gib.prejsnje_od, 'do': gib.prejsnje_do })} /> : <Prazno besedilo="Seznam ni prikazan — podatki ne segajo v prejšnje obdobje." />}
                </div>
              </div>
            </Okvir>
          </div>

          <Okvir naslov="Prihodki" opomba="knjižbe prihodkov (razred 7), brez DDV · klik odpre račune"
            desno={
              <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden text-xs">
                {RAZCL_P.map((r) => (
                  <button key={r.id} type="button" onClick={() => setRazcl(r.id)}
                    className={'px-2 py-1 ' + (razcl === r.id ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300')}>
                    {r.naziv}
                  </button>
                ))}
              </div>
            }>
            <Lestvica tocke={podatki.razcl.tocke} ostalo={podatki.razcl.ostalo} barva={BARVA.prihodki}
              oznaka={(t: any) => (razcl === 'konto' ? `${t.kljuc} ${t.naziv || ''}` : (t.naziv || t.kljuc))}
              onKlik={(t: any) => odpri('Prihodki — ' + (razcl === 'konto' ? t.kljuc : t.naziv), { [razcl]: t.kljuc })} />
          </Okvir>

        </>
      )}

      {drill && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={() => setDrill(null)}>
          <div className="h-full w-full max-w-3xl bg-white dark:bg-gray-900 shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
              <div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{drill.naslov}</div>
                <div className="text-[11px] text-gray-400">{drill.lijak ? 'dokumenti iz Izdaje' : `${fmtDatum(drill.params?.od || od)} – ${fmtDatum(drill.params?.do || doDat)}`}</div>
              </div>
              <div className="flex items-center gap-2">
                {!drill.lijak && (
                  <button type="button" onClick={() => navigate('/dokumenti?tip=izdani_racun')}
                    className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                    <ExternalLink className="w-3.5 h-3.5" /> Odpri seznam izdanih
                  </button>
                )}
                <button type="button" onClick={() => setDrill(null)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              {drill.nalaganje && <div className="p-6 flex justify-center"><Loader className="w-6 h-6 animate-spin text-blue-500" /></div>}
              {drill.napaka && <div className="p-4 text-sm text-rose-600">{drill.napaka}</div>}
              {drill.podatki && drill.lijak && (
                (drill.podatki.dokumenti || []).length ? (
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 text-gray-500">
                      <tr>
                        <th className="text-left px-3 py-2">Datum</th>
                        <th className="text-left px-2 py-2">Dokument</th>
                        <th className="text-left px-2 py-2">Kupec</th>
                        <th className="text-left px-2 py-2">Velja do / rok</th>
                        <th className="text-right px-2 py-2">Osnova</th>
                        <th className="text-right px-2 py-2">Skupaj</th>
                        <th className="text-left px-3 py-2">Stanje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drill.podatki.dokumenti.map((r: any) => (
                        <tr key={r.id} onClick={() => navigate(`/izdaja/${r.id}`)} className="border-t border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-950">
                          <td className="px-3 py-1.5 whitespace-nowrap">{fmtDatum(r.datum)}</td>
                          <td className="px-2 py-1.5">{TIP_IME[r.tip] || r.tip} {r.stevilka || ''}</td>
                          <td className="px-2 py-1.5 truncate max-w-[14rem]">{r.kupec || '—'}</td>
                          <td className="px-2 py-1.5 whitespace-nowrap">{fmtDatum(r.rok)}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{fmt(r.osnova)}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{fmt(r.skupaj)}</td>
                          <td className="px-3 py-1.5 text-gray-500">
                            {r.status}{r.tip === 'predracun' ? ` · ${r.placilo_status}` : ''}
                            {drill.lijak === 'ponudbe_obdobje' && (r.pretvorjen ? ` · račun${r.dni_do_racuna != null ? ` po ${r.dni_do_racuna} d` : ''}` : ' · brez računa')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <Prazno besedilo="Ni dokumentov v tej skupini." />
              )}
              {drill.podatki && !drill.lijak && (
                drill.podatki.racuni.length ? (
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 text-gray-500">
                      <tr>
                        <th className="text-left px-3 py-2">Datum</th>
                        <th className="text-left px-2 py-2">Številka</th>
                        <th className="text-left px-2 py-2">Interna</th>
                        <th className="text-left px-2 py-2">Kupec</th>
                        <th className="text-right px-2 py-2">Osnova</th>
                        <th className="text-right px-2 py-2">Skupaj</th>
                        <th className="text-left px-3 py-2">Stanje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drill.podatki.racuni.map((r: any) => (
                        <tr key={r.id} className="border-t border-gray-100 dark:border-gray-800">
                          <td className="px-3 py-1.5 whitespace-nowrap">{fmtDatum(r.datum)}</td>
                          <td className="px-2 py-1.5">{r.stevilka || '—'}{r.doc_type === 'izdani_dobropis' && <span className="ml-1 text-[10px] text-rose-600">dobropis</span>}</td>
                          <td className="px-2 py-1.5 text-gray-500">{r.interna || ''}</td>
                          <td className="px-2 py-1.5 truncate max-w-[14rem]">{r.partner || '—'}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{fmt(r.osnova)}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{fmt(r.skupaj)}</td>
                          <td className="px-3 py-1.5 text-gray-500">{r.status === 'approved' ? 'potrjen' : 'nepotrjen'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <Prazno besedilo="Ni računov za ta izbor." />
              )}
            </div>
            {drill.podatki && (
              <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-800 text-xs text-gray-600 dark:text-gray-300 flex flex-wrap gap-x-4">
                <span>{drill.lijak ? 'Dokumentov' : 'Računov'}: {drill.podatki.st}{drill.podatki.omejeno ? ` (prikazanih ${drill.podatki.racuni.length})` : ''}</span>
                <span>Osnova: {eur(drill.podatki.osnova)}</span>
                <span>Skupaj: {eur(drill.podatki.skupaj)}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
